import countryArray from "assets/json/country.json";

interface AddressItem {
  id: string;
  geocode?: GeocodeData;
  latitude?: number;
  longitude?: number;
  quality?: number;
  oversea?: boolean;
  spatial?: string;
  [key: string]: any;
}

interface GeocodeData {
  city: string | string[];
  country: string | string[];
  district: string | string[];
  formatted_address: string | string[];
  location: any;
  number: string | string[];
  province: string | string[];
  street: string | string[];
  postcode: string | string[];
}

const getValueOrEmpty = (value: any): string =>
  Array.isArray(value) && value.length === 0 ? "" : String(value);

const extractGeocodeData = (geocode: GeocodeData) => {
  const country = getValueOrEmpty(geocode.country);
  const district = getValueOrEmpty(geocode.district);
  return {
    city: getValueOrEmpty(geocode.city),
    country,
    district,
    address: getValueOrEmpty(geocode.formatted_address).replace(
      "undefined",
      ""
    ),
    location: getValueOrEmpty(geocode.location),
    number: getValueOrEmpty(geocode.number),
    province: getValueOrEmpty(geocode.province),
    street: getValueOrEmpty(geocode.street),
    zipCode: getValueOrEmpty(geocode.postcode).replace(
      "undefined",
      ""
    ),
    countryISO3: countryArray.find((item) =>
      item.label.toLowerCase().includes(country.toLowerCase())
    )?.iso3 ?? "",
    county: district,
    place: getValueOrEmpty(geocode.city)
  };
};

const processItem = (item: AddressItem, dataMap: Map<string, AddressItem>) => {
  const matchingData = dataMap.get(item.id);

  if (!matchingData) {
    return item;
  }

  const { geocode, latitude, longitude, quality, oversea, spatial } =
    matchingData;
  const coordinateSystem = spatial === "gcj" ? "gcj02" : "wgs84";
  const coordinateSource = "geoEncoded";

  const baseInfo = {
    ...item,
    latitude,
    longitude,
    quality,
    oversea,
    coordinateSource,
    coordinateSystem
  };

  if (!geocode) {
    return baseInfo;
  }

  return {
    ...baseInfo,
    ...extractGeocodeData(geocode)
  };
};

export const categorizeAddressData = (
  items: AddressItem[],
  data: Readonly<AddressItem[]>
) => {
  const processAddressEncodingData = (
    items: AddressItem[],
    data: Readonly<AddressItem[]>
  ): AddressItem[] => {
    const dataMap = new Map(data.map((item) => [item.id, item]));
    return items.map((item) => processItem(item, dataMap));
  };

  const processedItems = processAddressEncodingData(items, data);

  return processedItems.reduce(
    (acc, item) => {
      if (!item) return acc;

      if (item.quality === 0 || item.quality === undefined || item.quality === null) {
        acc.noMatch.push(item);
      } else if (item.quality && item.quality >= 4) {
        acc.match.push(item);
      } else {
        acc.candidates.push(item);
      }
      return acc;
    },
    {
      candidates: [] as AddressItem[],
      noMatch: [] as AddressItem[],
      match: [] as AddressItem[]
    }
  );
};
